// Client register: where each client came from (source, referred by) and since when they are a client.
// Pure functions over the workspace view; the UI prints and exports what `clientRegister` returns.
import type { WorkspaceView } from './changeset.ts';
import type { Person, PersonStage } from './schema.ts';
import type { Day } from './dates.ts';
import { searchKey, isActivePolicy } from './rules.ts';
import { annualNow } from './policy-engine.ts';

/** Standard sources, in the order the advisor picks them. A person's `source` holds one of these keys, or the
 *  advisor's own text (kept as typed). Labels: i18n `growth_a.source.<key>`. */
export const CLIENT_SOURCES = ['referral', 'friends_family', 'cold_call', 'professional_network', 'walk_in', 'website', 'social', 'event', 'campaign', 'portfolio_transfer',
  // ideas track 43: met in person (e.g. a business card scanned in quick add)
  'met_in_person'] as const;
export type ClientSource = (typeof CLIENT_SOURCES)[number];

// what advisors already typed in older records, mapped to a standard source (accent/case-insensitive, whole text)
const SYNONYMS: Record<ClientSource, string[]> = {
  referral: ['referral', 'referrals', 'referred', 'recommendation', 'συσταση', 'συστασεις', 'απο συσταση', 'με συσταση', 'συστηση'],
  friends_family: ['friends', 'family', 'friends and family', 'friends & family', 'warm market', 'φιλοι', 'φιλικα προσωπα', 'φιλικο προσωπο', 'φιλικα', 'οικογενεια', 'συγγενεις', 'φιλοι και συγγενεις', 'φιλικα και οικογενειακα προσωπα', 'γνωστοι', 'γνωστος'],
  cold_call: ['cold call', 'cold calls', 'cold calling', 'ψυχρη κληση', 'ψυχρες κλησεις', 'τηλεφωνα', 'τηλεφωνο', 'τηλεφωνα στην τυχη', 'τηλεφωνα τυχαια', 'τυχαια τηλεφωνα', 'τηλεφωνικη επαφη'],
  professional_network: ['professional network', 'business network', 'networking', 'colleague', 'επαγγελματικες γνωριμιες', 'επαγγελματικη γνωριμια', 'συναδελφος', 'συνεργατης'],
  walk_in: ['walk in', 'walk-in', 'office visit', 'επισκεψη', 'επισκεψη στο γραφειο', 'γραφειο'],
  website: ['website', 'web', 'site', 'avrastro.com', 'farazisgiannis.com', 'ιστοσελιδα', 'ιστοτοπος', 'internet', 'διαδικτυο'],
  social: ['social', 'social media', 'facebook', 'instagram', 'linkedin', 'tiktok', 'μεσα κοινωνικης δικτυωσης'],
  event: ['event', 'seminar', 'εκδηλωση', 'σεμιναριο', 'παρουσιαση'],
  campaign: ['campaign', 'advertising', 'ad', 'καμπανια', 'διαφημιση'],
  portfolio_transfer: ['portfolio transfer', 'transfer', 'inherited', 'μεταφορα', 'μεταφορα χαρτοφυλακιου', 'αναληψη χαρτοφυλακιου'],
  met_in_person: ['met in person', 'in person', 'business card', 'card', 'γνωριμια', 'προσωπικη γνωριμια', 'επαγγελματικη καρτα', 'καρτα', 'καρτα επισκεψης'],
};
const LOOKUP = new Map<string, ClientSource>();
for (const k of CLIENT_SOURCES) { LOOKUP.set(k, k); for (const s of SYNONYMS[k]) LOOKUP.set(searchKey(s), k); }

export interface SourceInfo {
  /** a standard source key, `custom` for the advisor's own text, or `unknown` when nothing was recorded */
  key: ClientSource | 'custom' | 'unknown';
  /** the advisor's own text for `custom` (as typed), else null */
  text: string | null;
  /** the grouping key: the standard key, `custom:<normalised text>`, or `unknown` */
  group: string;
  /** "Σύσταση από Γιώργο" / "Referred by George" typed as the source: the name, for display only */
  referrer?: string;
}
const REFERRED_BY = /^\s*(?:σ[υύ]σταση\s*(?:απ[οό]|:)|referral\s+from|referred\s+by|recommended\s+by)\s*:?\s*(.+?)\s*$/iu;
/** Read a stored source: standard keys and the common ways advisors wrote them map to the standard source. */
export function sourceInfo(raw: string | null | undefined): SourceInfo {
  const text = String(raw ?? '').trim();
  if (!text) return { key: 'unknown', text: null, group: 'unknown' };
  const std = LOOKUP.get(searchKey(text).replace(/_/g, ' ')) ?? LOOKUP.get(text);
  if (std) return { key: std, text: null, group: std };
  const ref = REFERRED_BY.exec(text);
  if (ref?.[1]) return { key: 'referral', text: null, group: 'referral', referrer: ref[1] };
  return { key: 'custom', text, group: `custom:${searchKey(text)}` };
}
/** A person's source; someone with a "referred by" and no recorded source counts as a referral. */
export function personSource(p: Pick<Person, 'source' | 'referredByPersonId' | 'referredByName'>): SourceInfo {
  const s = sourceInfo(p.source);
  if (s.key === 'unknown' && (p.referredByPersonId || p.referredByName)) return { key: 'referral', text: null, group: 'referral' };
  return s;
}
/** The advisor's own sources already in use (for the picker), most used first. */
export function customSources(v: WorkspaceView): string[] {
  const n = new Map<string, { text: string; count: number }>();
  for (const p of v.rows('persons')) {
    const s = sourceInfo(p.source);
    if (s.key !== 'custom') continue;
    const cur = n.get(s.group) ?? { text: s.text!, count: 0 };
    cur.count++; n.set(s.group, cur);
  }
  return [...n.values()].sort((a, b) => b.count - a.count || a.text.localeCompare(b.text)).map((x) => x.text);
}

export type SinceBasis = 'recorded' | 'first_policy' | 'added';
/** Since when a person is a client: the recorded date; else the first policy's issue/start date; else the day they
 *  were added to AVRASTRO. The basis is always returned, so a derived date is never shown as if it had been recorded. */
export function clientSinceOf(v: WorkspaceView, p: Person): { date: Day | null; basis: SinceBasis | null } {
  if (p.clientSince) return { date: p.clientSince.slice(0, 10), basis: 'recorded' };
  let first: string | null = null;
  for (const pol of v.where('policies', 'personId', p.id)) {
    const d = (pol.issueDate || pol.startDate || '').slice(0, 10);
    if (d && (!first || d < first)) first = d;
  }
  if (first) return { date: first, basis: 'first_policy' };
  if (p.stage === 'client' || p.stage === 'former') return { date: p.createdAt.slice(0, 10), basis: 'added' };
  return { date: null, basis: null };
}

export interface RegisterRow {
  id: string; name: string; stage: PersonStage; phone: string | null; email: string | null; city: string | null;
  since: Day | null; basis: SinceBasis | null;
  source: SourceInfo; referredBy: string | null; referredById: string | null;
  activePolicies: number; annualPremium: number;
}
export interface RegisterGroup { group: string; key: SourceInfo['key']; text: string | null; count: number; share: number }
export interface ClientRegister {
  rows: RegisterRow[];
  bySource: RegisterGroup[];
  byYear: { year: string; count: number }[];
  missing: { source: number; since: number };
}
export interface RegisterOptions {
  stages?: PersonStage[];
  /** a grouping key from `sourceInfo().group`, or empty for all */
  source?: string;
  from?: Day | null; to?: Day | null;
  sort?: 'name' | 'since' | 'source';
  includeArchived?: boolean;
  /** locale-aware compare for names (UI passes i18n.compare) */
  compare?: (a: string, b: string) => number;
}
/** Every client with since-when and where they came from, plus counts by source and by year. Nothing is estimated:
 *  a derived date carries its basis, an unrecorded source is "unknown". */
export function clientRegister(v: WorkspaceView, today: Day, o: RegisterOptions = {}): ClientRegister {
  const stages = o.stages?.length ? o.stages : (['client'] as PersonStage[]);
  const cmp = o.compare ?? ((a: string, b: string) => a.localeCompare(b));
  const all: RegisterRow[] = [];
  for (const p of v.rows('persons')) {
    if (p.stage === 'dependent' || !stages.includes(p.stage)) continue;
    if (p.archivedAt && !o.includeArchived) continue;
    const since = clientSinceOf(v, p);
    const active = v.where('policies', 'personId', p.id).filter(isActivePolicy);
    const ref = p.referredByPersonId ? v.get('persons', p.referredByPersonId) : undefined;
    all.push({
      id: p.id, name: p.displayName, stage: p.stage, phone: p.primaryPhone ?? null, email: p.primaryEmail ?? null, city: p.city ?? null,
      since: since.date, basis: since.basis, source: personSource(p), referredBy: ref?.displayName ?? p.referredByName ?? sourceInfo(p.source).referrer ?? null, referredById: ref?.id ?? null,
      activePolicies: active.length, annualPremium: Math.round(active.reduce((s, x) => s + annualNow(v, x, today), 0) * 100) / 100,
    });
  }
  const rows = all
    .filter((r) => !o.source || r.source.group === o.source)
    .filter((r) => (!o.from || (!!r.since && r.since >= o.from)) && (!o.to || (!!r.since && r.since <= o.to)));
  const order = (r: RegisterRow) => (r.source.key === 'unknown' ? 99 : r.source.key === 'custom' ? 50 : CLIENT_SOURCES.indexOf(r.source.key as ClientSource));
  rows.sort((a, b) => {
    if (o.sort === 'since') return (a.since ?? '9999').localeCompare(b.since ?? '9999') || cmp(a.name, b.name);
    if (o.sort === 'source') return order(a) - order(b) || (a.source.text ?? '').localeCompare(b.source.text ?? '') || cmp(a.name, b.name);
    return cmp(a.name, b.name);
  });
  const groups = new Map<string, RegisterGroup>();
  for (const r of rows) {
    const g = groups.get(r.source.group) ?? { group: r.source.group, key: r.source.key, text: r.source.text, count: 0, share: 0 };
    g.count++; groups.set(r.source.group, g);
  }
  const bySource = [...groups.values()].map((g) => ({ ...g, share: rows.length ? Math.round((g.count / rows.length) * 1000) / 10 : 0 }))
    .sort((a, b) => b.count - a.count || order({ source: { key: a.key } } as RegisterRow) - order({ source: { key: b.key } } as RegisterRow));
  const years = new Map<string, number>();
  for (const r of rows) if (r.since) years.set(r.since.slice(0, 4), (years.get(r.since.slice(0, 4)) ?? 0) + 1);
  return {
    rows, bySource,
    byYear: [...years.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([year, count]) => ({ year, count })),
    missing: { source: rows.filter((r) => r.source.key === 'unknown').length, since: rows.filter((r) => r.basis !== 'recorded').length },
  };
}
